import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './FightCards.module.scss';
import { fetchFightCardEventDetail, fetchFightCardEvents } from '../../../services/fight-cards.service';
import { EventDetail, EventSummary, FightCardEventsResponse, FightCardItem, PaginationMeta } from '../../../types/fight-cards.types';
import { Routes } from '../../../types/constants';
import FighterCornerBars from '../../FighterCornerBars/FighterCornerBars';
import { formatVideoCardDate, formatVideoCardFightNumber } from '../../../utils/video-card';
import { VideoCardParticipant } from '../../../types/video-card.types';
import { orderVideoCardParticipants } from '../../../utils/video-card';
import { usePurchaseStatus } from '../../../providers/purchase-status.provider';
import { usePurchaseFlow } from '../../../hooks/usePurchaseFlow';
import PageHeader from '../../PageHeader/PageHeader';
import { useWatchState } from '../../../providers/watch-state.provider';
import { buildResumeUrlForCard } from '../../../utils/watch-state';

const FIGHT_CARD_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const buildParticipantsFromItem = (item: FightCardItem): VideoCardParticipant[] => {
    if (item.participants && item.participants.length > 0) {
        return item.participants.map((participant) => ({
            name: participant.ParticipantName,
            cornerColor: participant.CornerColor ?? null
        }));
    }

    if (item.type === 'fight') {
        return [
            { name: item.fighterA ?? 'TBD', cornerColor: null },
            { name: item.fighterB ?? 'TBD', cornerColor: null }
        ];
    }

    if (item.guest) {
        return [{ name: item.guest, cornerColor: null }];
    }

    return [{ name: item.title, cornerColor: null }];
};

const FightCards = () => {
    const { isVideoOwned } = usePurchaseStatus();
    const { startPurchase, isProcessing: isPurchaseProcessing } = usePurchaseFlow();
    const { getWatchStateByIdentity } = useWatchState();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [decadeFilter, setDecadeFilter] = useState('all');
    const [events, setEvents] = useState<EventSummary[]>([]);
    const [pagination, setPagination] = useState<PaginationMeta | null>(null);
    const [availableDecades, setAvailableDecades] = useState<number[]>([]);
    const [expandedEventDetails, setExpandedEventDetails] = useState<Record<string, EventDetail>>({});
    const [expandedLoadingById, setExpandedLoadingById] = useState<Record<string, boolean>>({});
    const [expandedErrorById, setExpandedErrorById] = useState<Record<string, string | null>>({});
    const [expandedOpenById, setExpandedOpenById] = useState<Record<string, boolean>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
    const activeListRequestRef = useRef<AbortController | null>(null);

    const decadeOptions = useMemo(() => {
        const decades = new Set<number>(availableDecades);
        if (decades.size === 0) {
            events.forEach((event) => {
                const year = new Date(event.eventDate).getFullYear();
                const decade = Math.floor(year / 10) * 10;
                decades.add(decade);
            });
        }
        return Array.from(decades)
            .sort((a, b) => b - a)
            .map((decade) => ({
                value: `${decade}s`,
                label: `${decade}s`
            }));
    }, [availableDecades, events]);

    const loadExpandedEvent = async (eventId: string) => {
        if (!eventId || expandedEventDetails[eventId] || expandedLoadingById[eventId]) {
            return;
        }

        setExpandedLoadingById((prev) => ({ ...prev, [eventId]: true }));
        setExpandedErrorById((prev) => ({ ...prev, [eventId]: null }));

        try {
            const response = await fetchFightCardEventDetail(eventId, false);
            if (!response?.id) {
                setExpandedErrorById((prev) => ({ ...prev, [eventId]: 'Unable to load event lineup.' }));
                return;
            }

            setExpandedEventDetails((prev) => ({
                ...prev,
                [eventId]: {
                    ...response,
                    items: response.items ?? []
                }
            }));
        } catch (error) {
            setExpandedErrorById((prev) => ({ ...prev, [eventId]: 'Unable to load event lineup.' }));
        } finally {
            setExpandedLoadingById((prev) => ({ ...prev, [eventId]: false }));
        }
    };

    useEffect(() => {
        const debounceTimer = window.setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
        }, SEARCH_DEBOUNCE_MS);

        return () => window.clearTimeout(debounceTimer);
    }, [searchTerm]);

    const loadEvents = useCallback(async (page: number, append: boolean) => {
        activeListRequestRef.current?.abort();
        const requestController = new AbortController();
        activeListRequestRef.current = requestController;

        if (append) {
            setIsLoadingMore(true);
            setLoadMoreError(null);
        } else {
            setIsLoading(true);
            setIsLoadingMore(false);
            setErrorMessage(null);
            setLoadMoreError(null);
        }

        try {
            const decadeValue = decadeFilter === 'all'
                ? undefined
                : Number(decadeFilter.replace('s', ''));

            const response: FightCardEventsResponse = await fetchFightCardEvents(
                {
                    search: debouncedSearchTerm || undefined,
                    decade: Number.isNaN(decadeValue) ? undefined : decadeValue,
                    page,
                    pageSize: FIGHT_CARD_PAGE_SIZE,
                    sort: 'eventDate:desc'
                },
                undefined,
                requestController.signal
            );

            if (requestController.signal.aborted) {
                return;
            }

            const visibleEvents = (response.items ?? []).filter((item) => !item.hideFromFightCards);
            setEvents((currentEvents) => {
                if (!append) {
                    return visibleEvents;
                }

                const existingIds = new Set(currentEvents.map((event) => event.id));
                return [
                    ...currentEvents,
                    ...visibleEvents.filter((event) => !existingIds.has(event.id))
                ];
            });
            setPagination(response.pagination ?? null);
            setAvailableDecades(response.availableDecades ?? []);

            if (!append) {
                setExpandedEventDetails({});
                setExpandedLoadingById({});
                setExpandedErrorById({});
                setExpandedOpenById({});
            }
        } catch (error) {
            if ((error as { name?: string })?.name === 'AbortError' || requestController.signal.aborted) {
                return;
            }

            if (append) {
                setLoadMoreError('Unable to load more fight cards. Please try again.');
            } else {
                setErrorMessage('We ran into an issue loading fight cards. Please try again soon.');
                setEvents([]);
                setPagination(null);
                setAvailableDecades([]);
            }
        } finally {
            if (activeListRequestRef.current === requestController) {
                activeListRequestRef.current = null;
                if (append) {
                    setIsLoadingMore(false);
                } else {
                    setIsLoading(false);
                }
            }
        }
    }, [debouncedSearchTerm, decadeFilter]);

    useEffect(() => {
        void loadEvents(1, false);
        return () => {
            activeListRequestRef.current?.abort();
        };
    }, [loadEvents]);

    const handlePurchase = async (videoId: string) => {
        await startPurchase(videoId);
    };

    return (
        <div className={styles.page}>
            <PageHeader title="Event Fight Cards" subtitle="Browse event lineups, interviews, and featured matchups.">
                <div className={styles.filters}>
                    <div className={styles.search}>
                        <span className="material-symbols-outlined">search</span>
                        <input
                            type="text"
                            aria-label="Search fight cards"
                            placeholder="Search events, fighters, or interviews"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                        />
                    </div>
                    <label className={styles.filter}>
                        <span>Decade</span>
                        <select value={decadeFilter} onChange={(event) => setDecadeFilter(event.target.value)}>
                            <option value="all">All decades</option>
                            {decadeOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
            </PageHeader>

            <section className={styles.content}>
                <div className={styles.events} aria-busy={isLoading || isLoadingMore}>
                    {isLoading ? (
                        Array.from({ length: 3 }).map((_, index) => (
                            <div key={`skeleton-${index}`} className={styles.eventCard}>
                                <div className={styles.eventSummary}>
                                    <div className={styles.eventSummaryInfo}>
                                        <div className={styles.thumbnailSkeleton} />
                                        <div className={styles.skeletonBlock}>
                                            <div className={styles.skeletonLine} />
                                            <div className={styles.skeletonLineShort} />
                                        </div>
                                    </div>
                                    <div className={styles.eventMeta}>
                                        <div className={styles.skeletonLineSmall} />
                                        <div className={styles.skeletonPill} />
                                    </div>
                                </div>
                                <div className={styles.eventBody}>
                                    <div className={styles.skeletonLine} />
                                    <div className={styles.skeletonLineShort} />
                                </div>
                            </div>
                        ))
                    ) : errorMessage ? (
                        <div className={styles.stateMessage}>
                            <h2>Unable to load fight cards</h2>
                            <p>{errorMessage}</p>
                        </div>
                    ) : events.length === 0 ? (
                        <div className={styles.stateMessage}>
                            <h2>No fight cards found</h2>
                            <p>Try adjusting your search or decade filter to find other events.</p>
                        </div>
                    ) : (
                        events.map((event, index) => {
                            const expandedEvent = expandedEventDetails[event.id];
                            const isExpandedLoading = expandedLoadingById[event.id] ?? false;
                            const expandedError = expandedErrorById[event.id];

                            return (
                            <details
                                key={event.id}
                                className={styles.eventCard}
                                open={expandedOpenById[event.id] ?? false}
                                onToggle={(toggleEvent) => {
                                    const detailsElement = toggleEvent.currentTarget;
                                    if (!detailsElement) {
                                        return;
                                    }
                                    setExpandedOpenById((prev) => ({
                                        ...prev,
                                        [event.id]: detailsElement.open
                                    }));
                                    if (detailsElement.open) {
                                        void loadExpandedEvent(event.id);
                                    }
                                }}
                            >
                                <summary className={styles.eventSummary}>
                                    <div className={styles.eventSummaryInfo}>
                                        {event.thumbnailUrl ? (
                                            <img
                                                className={styles.thumbnail}
                                                src={event.thumbnailUrl}
                                                alt={`${event.eventName} thumbnail`}
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className={styles.thumbnailSkeleton} />
                                        )}
                                        <div>
                                            <h2>{event.eventName}</h2>
                                            <p>{[event.venueName, event.location].filter(Boolean).join(' · ')}</p>
                                            {event.tags && event.tags.length > 0 ? (
                                                <div className={styles.tagRow}>
                                                    {event.tags.map((tag) => (
                                                        <span key={tag} className={styles.tagChip}>#{tag}</span>
                                                    ))}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className={styles.eventMeta}>
                                        <span>{new Date(event.eventDate).toLocaleDateString()}</span>
                                        <Link to={`${Routes.FIGHT_CARD_EVENT}/${event.id}`} className={styles.viewLink}>
                                            View Event
                                        </Link>
                                    </div>
                                </summary>
                                <div className={styles.eventBody}>
                                    {(event.eventUrl || event.venueUrl) ? (
                                        <div className={styles.eventLinks}>
                                            {event.eventUrl ? (
                                                <a href={event.eventUrl} target="_blank" rel="noreferrer">Event site</a>
                                            ) : null}
                                            {event.venueUrl ? (
                                                <a href={event.venueUrl} target="_blank" rel="noreferrer">Venue info</a>
                                            ) : null}
                                        </div>
                                    ) : null}

                                    {isExpandedLoading ? (
                                        <p className={styles.itemMeta}>Loading lineup...</p>
                                    ) : expandedError ? (
                                        <p className={styles.itemMeta}>{expandedError}</p>
                                    ) : expandedEvent ? (
                                        expandedEvent.items.length === 0 ? (
                                            <p className={styles.itemMeta}>No fights or interviews listed yet.</p>
                                        ) : (
                                            <ul className={styles.itemList}>
                                                {[...expandedEvent.items]
                                                    .sort((left, right) => (left.order ?? Number.MAX_SAFE_INTEGER) - (right.order ?? Number.MAX_SAFE_INTEGER))
                                                    .map((item) => {
                                                        const participants = buildParticipantsFromItem(item);
                                                        const orderedParticipants = orderVideoCardParticipants(participants);
                                                        const leftParticipant = orderedParticipants[0];
                                                        const rightParticipant = orderedParticipants[1];
                                                        const fightNumberLabel = formatVideoCardFightNumber(item.order ?? null);
                                                        const dateLabel = formatVideoCardDate(expandedEvent.eventDate);
                                                        const metaLine = [fightNumberLabel, dateLabel].filter(Boolean).join(' | ');
                                                        const isOwned = isVideoOwned(item.id);
                                                        const hasValidPurchaseId = /^[0-9a-fA-F-]{36}$/.test(item.id);
                                                        const canPurchase = hasValidPurchaseId && !isOwned;
                                                        const watchState = getWatchStateByIdentity(item.id, item.watchUrl);
                                                        const itemWatchUrl = item.watchUrl && watchState?.kind === 'in-progress'
                                                            ? buildResumeUrlForCard(item.watchUrl, watchState.resumeSeconds)
                                                            : item.watchUrl;

                                                        return (
                                                            <li key={item.id} className={styles.item}>
                                                                <div className={styles.itemContent}>
                                                                    <div className={styles.itemHeader}>
                                                                        <span className={item.type === 'fight' ? styles.fightTag : styles.interviewTag}>
                                                                            {item.type === 'fight' ? 'Fight' : 'Interview'}
                                                                        </span>
                                                                        <FighterCornerBars
                                                                            leftName={leftParticipant?.name ?? 'TBD'}
                                                                            leftColor={leftParticipant?.cornerColor ?? null}
                                                                            rightName={rightParticipant?.name ?? 'TBD'}
                                                                            rightColor={rightParticipant?.cornerColor ?? null}
                                                                            layout="row"
                                                                            size="sm"
                                                                        />
                                                                        {isOwned ? (
                                                                            <span className={styles.ownedBadge}>Owned</span>
                                                                        ) : null}
                                                                        {watchState ? (
                                                                            <span className={`${styles.watchStateBadge} ${watchState.kind === 'watched' ? styles.watchStateBadgeComplete : ''}`}>
                                                                                {watchState.kind === 'in-progress' ? 'Resume' : 'Watched'}
                                                                            </span>
                                                                        ) : null}
                                                                    </div>
                                                                    <h3 className={styles.itemTitle}>{item.title}</h3>
                                                                    {metaLine ? <p className={styles.itemMeta}>{metaLine}</p> : null}
                                                                    {watchState ? (
                                                                        <p className={styles.itemWatchStateMeta}>
                                                                            {watchState.kind === 'in-progress'
                                                                                ? `Resume at ${watchState.resumeLabel}`
                                                                                : `Watched ${watchState.lastWatchedLabel}`}
                                                                        </p>
                                                                    ) : null}
                                                                </div>
                                                                <div className={styles.itemActions}>
                                                                    {itemWatchUrl ? (
                                                                        <Link to={itemWatchUrl} className={styles.watchLink}>
                                                                            {watchState?.kind === 'in-progress' ? 'Resume' : 'Watch'}
                                                                        </Link>
                                                                    ) : null}
                                                                    {canPurchase ? (
                                                                        <button
                                                                            type="button"
                                                                            className={styles.buyLink}
                                                                            disabled={isPurchaseProcessing}
                                                                            onClick={() => handlePurchase(item.id)}
                                                                        >
                                                                            {isPurchaseProcessing ? 'Starting checkout...' : 'Buy Now'}
                                                                        </button>
                                                                    ) : null}
                                                                    {item.venueUrl ? (
                                                                        <a href={item.venueUrl} target="_blank" rel="noreferrer" className={styles.venueLink}>
                                                                            Venue
                                                                        </a>
                                                                    ) : null}
                                                                </div>
                                                            </li>
                                                        );
                                                    })}
                                            </ul>
                                        )
                                    ) : (
                                        <p className={styles.itemMeta}>Expand this event to load fights and interviews.</p>
                                    )}
                                </div>
                            </details>
                        );
                        })
                    )}
                    {!isLoading && !errorMessage && events.length > 0 && pagination?.hasNextPage ? (
                        <div className={styles.pagination}>
                            <button
                                type="button"
                                className={styles.loadMoreButton}
                                disabled={isLoadingMore}
                                onClick={() => void loadEvents(pagination.page + 1, true)}
                            >
                                {isLoadingMore ? 'Loading more...' : 'Load more fight cards'}
                            </button>
                            <span aria-live="polite">
                                Showing {events.length} of {pagination.total} events
                            </span>
                        </div>
                    ) : null}
                    {loadMoreError ? (
                        <p className={styles.loadMoreError} role="alert">{loadMoreError}</p>
                    ) : null}
                </div>
            </section>
        </div>
    );
};

export default FightCards;
